import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Package, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STEPS = [
  { key: "placed", label: "Order Placed" },
  { key: "processing", label: "Processing" },
  { key: "shipped", label: "Shipped" },
  { key: "transit", label: "In Transit" },
  { key: "out", label: "Out for Delivery" },
  { key: "delivered", label: "Delivered" },
];
const PROGRESS = { new: 1, in_review: 1, in_production: 2, shipped: 4, delivered: 6 };

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

function Timeline({ order }) {
  const done = PROGRESS[order.status] ?? 1;
  const stamps = {
    placed: fmtDate(order.created_at),
    processing: fmtDate(order.processing_at),
    shipped: fmtDate(order.shipped_at),
    delivered: fmtDate(order.delivered_at),
  };
  return (
    <ol className="grid grid-cols-1 sm:grid-cols-6 gap-4 sm:gap-0" data-testid="track-timeline">
      {STEPS.map((s, i) => {
        const complete = i < done;
        const current = i === done;
        return (
          <li key={s.key} className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-0 relative" data-testid={`track-step-${s.key}`}>
            {i > 0 && (
              <span className={`hidden sm:block absolute top-[9px] right-1/2 w-full h-[2px] -translate-x-4 sm:translate-x-0 sm:left-[-50%] ${i < done ? "bg-signal" : "bg-border"}`} />
            )}
            <span className={`relative z-[1] w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
              complete ? "bg-signal border-signal" : current ? "border-signal bg-ink" : "border-border bg-ink"}`}>
              {complete && <span className="w-1.5 h-1.5 rounded-full bg-ink" />}
            </span>
            <div className="sm:mt-3 sm:text-center">
              <p className={`font-mono text-[10px] uppercase tracking-[0.15em] ${complete || current ? "text-chalk" : "text-graphite"}`}>{s.label}</p>
              {stamps[s.key] && <p className="font-mono text-[10px] text-graphite mt-0.5">{stamps[s.key]}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Result({ order }) {
  const statusLabel = {
    new: "Order Placed", in_review: "In Review", in_production: "Processing",
    shipped: "Shipped — In Transit", delivered: "Delivered", cancelled: "Cancelled",
  }[order.status] || order.status;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="border border-border mt-10" data-testid="track-result">
      <div className="p-6 sm:p-8 border-b border-border flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Order</p>
          <h2 className="font-anton text-3xl uppercase" data-testid="track-order-number">{order.order_number}</h2>
        </div>
        <div className="text-right">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite">Placed {fmtDate(order.created_at)}</p>
          <p className={`font-mono text-xs uppercase tracking-[0.2em] mt-1 ${order.status === "cancelled" ? "text-pulse" : "text-signal"}`}
            data-testid="track-status">{statusLabel}</p>
        </div>
      </div>

      {order.status === "cancelled" ? (
        <div className="p-6 sm:p-8">
          <p className="text-pulse font-mono text-sm uppercase tracking-widest" data-testid="track-cancelled-note">
            This order was cancelled.
          </p>
        </div>
      ) : (
        <div className="p-6 sm:p-8 border-b border-border">
          <Timeline order={order} />
        </div>
      )}

      {(order.tracking_number || order.estimated_delivery_from || order.delivered_at) && order.status !== "cancelled" && (
        <div className="p-6 sm:p-8 border-b border-border grid sm:grid-cols-3 gap-6">
          {order.carrier && (
            <div>
              <p className="eyebrow mb-1">Carrier</p>
              <p className="font-mono text-sm" data-testid="track-carrier">{order.carrier}</p>
            </div>
          )}
          {order.tracking_number && (
            <div>
              <p className="eyebrow mb-1">Tracking number</p>
              <p className="font-mono text-sm text-signal break-all" data-testid="track-number">{order.tracking_number}</p>
            </div>
          )}
          {order.status === "delivered" && order.delivered_at ? (
            <div>
              <p className="eyebrow mb-1">Delivered</p>
              <p className="font-mono text-sm" data-testid="track-delivered-date">{fmtDate(order.delivered_at)}</p>
            </div>
          ) : order.estimated_delivery_from ? (
            <div>
              <p className="eyebrow mb-1">Estimated delivery</p>
              <p className="font-mono text-sm" data-testid="track-eta">
                {fmtDate(order.estimated_delivery_from)} – {fmtDate(order.estimated_delivery_to)}
              </p>
            </div>
          ) : null}
          {order.tracking_url && (
            <div className="sm:col-span-3">
              <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-view-full-btn"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider border border-chalk px-5 py-2.5 hover:bg-chalk hover:text-ink transition-colors">
                View Full Tracking Details <ExternalLink size={13} />
              </a>
            </div>
          )}
        </div>
      )}

      <div className="p-6 sm:p-8">
        <p className="eyebrow mb-4">Items</p>
        <div className="space-y-3" data-testid="track-items">
          {order.items.map((i) => (
            <div key={`${i.name}-${i.size}-${i.color}`} className="flex justify-between text-sm">
              <span>{i.quantity}× {i.name}
                <span className="text-graphite font-mono text-xs ml-2">{[i.size, i.color].filter(Boolean).join(" / ")}</span>
              </span>
            </div>
          ))}
        </div>
        {order.amount_total != null && (
          <div className="flex justify-between border-t border-border mt-4 pt-4">
            <span className="font-mono text-[11px] uppercase tracking-wider text-graphite">Total</span>
            <span className="font-mono text-sm text-signal">${Number(order.amount_total).toFixed(2)}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", { order_number: orderNumber, email });
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not look up that order. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="track-order-page">
      <div className="container-sync max-w-3xl">
        <p className="eyebrow mb-3">Order Tracking</p>
        <h1 className="font-anton uppercase text-4xl sm:text-5xl mb-4">Track My Order</h1>
        <p className="text-graphite text-sm max-w-lg mb-10">
          Enter your order number (from your confirmation email) and the email you used at checkout.
        </p>

        <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-4 items-end" data-testid="track-form">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite block mb-2">Order number</label>
            <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} required
              placeholder="SYNC-1001" data-testid="track-order-input"
              className="w-full bg-transparent border-b border-border py-2.5 font-mono text-sm focus:border-signal outline-none" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite block mb-2">Email used at checkout</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="you@example.com" data-testid="track-email-input"
              className="w-full bg-transparent border-b border-border py-2.5 font-mono text-sm focus:border-signal outline-none" />
          </div>
          <button type="submit" disabled={loading} data-testid="track-submit-btn"
            className="inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-wider bg-chalk text-ink px-6 py-3 hover:bg-signal transition-colors disabled:opacity-60">
            {loading ? "Searching…" : <>Track Order <Search size={13} /></>}
          </button>
        </form>

        {error && (
          <p className="mt-6 font-mono text-xs text-pulse" data-testid="track-error">{error}</p>
        )}

        {order && <Result order={order} />}

        {!order && !error && (
          <div className="mt-16 border border-dashed border-border p-8 flex items-start gap-4">
            <Package size={20} className="text-graphite shrink-0 mt-0.5" />
            <p className="text-graphite text-sm leading-relaxed">
              Every SYNC piece is made to order — production takes 3–7 business days, then shipping takes
              7–10 business days after production. Once your order ships you'll receive your carrier and
              tracking number by email — you can follow every step right here, or open the carrier's official
              tracking page.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
